import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import { STATUS_OPTIONS } from "@/lib/statuses";
import { DatePicker } from "@/components/DatePicker";

export default function EditProjectDialog({ open, onOpenChange, project, users, onSaved }) {
  const [form, setForm] = useState(project || {});

  // Refresh form when re-opened (or project changes)
  useEffect(() => {
    if (open) setForm(project || {});
  }, [open, project]);

  const save = async () => {
    try {
      await api.patch(`/projects/${project.id}`, form);
      toast.success("Project updated");
      onOpenChange(false);
      onSaved?.();
    } catch { toast.error("Failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl rounded-sm">
        <DialogHeader><DialogTitle style={{ fontFamily: "Outfit" }}>Edit Project</DialogTitle></DialogHeader>
        <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-2">
          <div><Label className="text-xs">Title</Label><Input className="mt-1 rounded-sm" value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
          <div><Label className="text-xs">Description</Label><Textarea className="mt-1 rounded-sm" value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
          <div><Label className="text-xs">To-Do / Scope</Label><Textarea className="mt-1 rounded-sm" value={form.todo || ""} onChange={(e) => setForm({ ...form, todo: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Due Date</Label><div className="mt-1"><DatePicker value={form.due_date || ""} onChange={(v) => setForm({ ...form, due_date: v })} testId="input-edit-proj-due" /></div></div>
            <div>
              <Label className="text-xs">Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Stuck With</Label>
              <Select value={form.stuck_with} onValueChange={(v) => setForm({ ...form, stuck_with: v })}>
                <SelectTrigger className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="None">None</SelectItem>
                  <SelectItem value="Us">Us</SelectItem>
                  <SelectItem value="Client">Client</SelectItem>
                  <SelectItem value="External">External</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Stuck Note</Label><Input className="mt-1 rounded-sm" value={form.stuck_note || ""} onChange={(e) => setForm({ ...form, stuck_note: e.target.value })} /></div>
          </div>
          <div>
            <Label className="text-xs">Assignees</Label>
            <div className="mt-1 border border-slate-200 rounded-sm p-2 max-h-40 overflow-y-auto space-y-1">
              {users.map((u) => (
                <label key={u.user_id} className="flex items-center gap-2 text-xs cursor-pointer">
                  <input
                    type="checkbox"
                    checked={(form.assignee_ids || []).includes(u.user_id)}
                    onChange={(e) => {
                      const ids = new Set(form.assignee_ids || []);
                      if (e.target.checked) ids.add(u.user_id); else ids.delete(u.user_id);
                      setForm({ ...form, assignee_ids: [...ids] });
                    }}
                  />
                  {u.name} <span className="text-slate-400 font-mono-data">({u.role})</span>
                </label>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="bg-[#0a2540] hover:bg-[#0f2f50] rounded-sm" onClick={save} data-testid="btn-save-project">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
